/**
 * useTitleHistory
 *
 * Hook para mantener un historial circular de títulos del tooltip.
 * Sincroniza con la dirección de navegación (up/down) para determinar el índice actual.
 *
 * Features:
 * - Historial circular con límite máximo (default: 10 títulos)
 * - Sincronización con dirección de navegación
 * - Reset del historial
 * - Manejo de primera renderización
 *
 * @example
 * ```typescript
 * const { titles, currentIndex, addTitle } = useTitleHistory({
 *   maxHistory: 10
 * });
 *
 * // Al cambiar de item
 * addTitle('New Item', 'down');
 * // titles = ['New Item']
 * // currentIndex = 0
 * ```
 */

import { useState, useCallback, useRef } from 'react'
import type { TooltipDirection } from './useTooltipDirection'

export interface UseTitleHistoryOptions {
  /** Máximo número de títulos en historial (default: 10) */
  maxHistory?: number
}

export interface UseTitleHistoryReturn {
  /** Array de títulos en el historial */
  titles: string[]
  /** Índice actual en el historial */
  currentIndex: number
  /** Agregar nuevo título según dirección de navegación */
  addTitle: (newTitle: string, direction: TooltipDirection) => void
  /** Resetear historial */
  reset: () => void
}

export function useTitleHistory({
  maxHistory = 10
}: UseTitleHistoryOptions = {}): UseTitleHistoryReturn {
  const [titles, setTitles] = useState<string[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const isFirstRenderRef = useRef(true)

  /**
   * Agregar nuevo título al historial
   *
   * Lógica:
   * - Primera vez: Agregar título y establecer índice 0
   * - Direction 'down': Avanzar (incrementar índice)
   * - Direction 'up': Retroceder (decrementar índice)
   * - Si el título ya existe en el historial, mover índice a esa posición
   * - Limitar historial a maxHistory títulos (FIFO circular)
   */
  const addTitle = useCallback(
    (newTitle: string, direction: TooltipDirection) => {
      // Primera renderización: inicializar con el primer título
      if (isFirstRenderRef.current) {
        setTitles([newTitle])
        setCurrentIndex(0)
        isFirstRenderRef.current = false
        return
      }

      setTitles((prevTitles) => {
        // Buscar si el título ya existe en el historial
        const existingIndex = prevTitles.findIndex(t => t === newTitle)

        if (existingIndex !== -1) {
          // El título ya existe, mover índice a esa posición
          setCurrentIndex(existingIndex)
          return prevTitles
        }

        // Título nuevo: agregar según dirección
        let newTitles: string[]
        let newIndex: number

        if (direction === 'down') {
          // Navegación hacia adelante: agregar al final
          newTitles = [...prevTitles, newTitle]
          newIndex = newTitles.length - 1
        } else {
          // Navegación hacia atrás: agregar al principio
          newTitles = [newTitle, ...prevTitles]
          newIndex = 0
        }

        // Limitar historial a maxHistory
        if (newTitles.length > maxHistory) {
          if (direction === 'down') {
            // Eliminar primeros elementos (más antiguos)
            newTitles = newTitles.slice(newTitles.length - maxHistory)
            newIndex = newTitles.length - 1
          } else {
            // Eliminar últimos elementos
            newTitles = newTitles.slice(0, maxHistory)
            newIndex = 0
          }
        }

        setCurrentIndex(newIndex)
        return newTitles
      })
    },
    [maxHistory]
  )

  /**
   * Resetear historial a estado inicial
   */
  const reset = useCallback(() => {
    setTitles([])
    setCurrentIndex(0)
    isFirstRenderRef.current = true
  }, [])

  return {
    titles,
    currentIndex,
    addTitle,
    reset,
  }
}
